// rcobchod add-to-cart (CARD.md "Cart button"): the SDK's own form-urlencoded AJAX cart POSTs the
// product to the cart page, then — only once that request succeeded — our hook refreshes the header
// cart totals and opens the shop's own "added to cart" modal. Replaces the GTM tag that did the same
// from the widgets' dataLayer AddToCart events (ACCOUNT.md: it must be switched off).
//
// Pure except for the browser calls in `afterAddToCart` (type-only SDK import), so `pnpm test` runs
// the rest under Node type stripping.

import type { SearchResultsConfig } from "@raventic/sdk";

type CartConfig = NonNullable<SearchResultsConfig["cartConfig"]>;
type AddedProduct = { id: string; name: string };

declare global {
  interface Window {
    // rcobchod's layout.js: fills and shows `#cartAddedModal` (with its related products). It writes
    // `nazev` into the modal as HTML — the caller escapes it.
    dokoncitObj?: (nazev: string, addId: string, souvisejiciIds: string) => void;
  }
}

// Relative on purpose: same origin as the page (www or not), so the session cookie goes along.
export const CART_URL = "/nakupni-kosik/";

// The header cart totals ("1 249 Kč"): desktop header, mobile header and mobile menu.
export const CART_TOTAL_SELECTOR = 'a[href="/nakupni-kosik/"] .text-orange';

// The cart page's add form, as the old GTM tag posted it. `{product}` / `{quantity}` are expanded by
// the SDK. `cena` must be present and non-zero, but the server prices the line from its own data.
export const CART_PARAMETERS = {
  addId: "{product}",
  addKs: "{quantity}",
  cena: "1",
  Koupit: "Do košíku",
} as const;

// For `nazev`, which dokoncitObj inserts as HTML (the shop's own PHP call escapes it the same way).
export const escapeHtml = (text: string): string =>
  text.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string);

type TextNode = { textContent: string | null };

// Writes the fresh total onto every total of the page. All of them show the same value, but the
// pages differ in how many they render (the cart page has no mobile menu copy), so no pairwise copy.
// No fresh total → the page's are left alone.
export const applyCartTotal = (fresh: ArrayLike<TextNode>, current: ArrayLike<TextNode>): void => {
  const total = fresh.length > 0 ? fresh[0].textContent : null;
  if (total === null) return;
  for (let i = 0; i < current.length; i++) current[i].textContent = total;
};

const refreshCartTotals = async (): Promise<void> => {
  const response = await fetch(CART_URL, { credentials: "same-origin" });
  if (!response.ok) throw new Error(`cart page HTTP ${response.status}`);
  const html = new DOMParser().parseFromString(await response.text(), "text/html");
  applyCartTotal(html.querySelectorAll(CART_TOTAL_SELECTOR), document.querySelectorAll(CART_TOTAL_SELECTOR));
};

// Runs after a successful add: the modal right away, the totals as soon as the cart page is fetched.
// Never throws (the SDK has already reported the add).
export const afterAddToCart = (product: AddedProduct): void => {
  try {
    window.dokoncitObj?.(escapeHtml(product.name), product.id, "");
  } catch (error) {
    console.error("[rcobchod-cz] dokoncitObj failed", error);
  }
  refreshCartTotals().catch((error: unknown) => console.error("[rcobchod-cz] cart totals refresh failed", error));
};

// The SDK calls `finalUrlPattern` only after the POST answered 2xx, and navigates only when it
// returns a URL — so it doubles as the success hook, and null keeps the visitor on the page.
export const cartConfig: CartConfig = {
  type: "AJAXFormUrlEncodedCart",
  urlPattern: CART_URL,
  parameters: CART_PARAMETERS,
  finalUrlPattern: (product) => {
    afterAddToCart(product);
    return null;
  },
};
